/**
 * AI Model Configuration
 *
 * Central, modular configuration for all AI generation models.
 * Each model declares its own capabilities and settings — the UI
 * renders controls dynamically based on this config.
 *
 * To add a new model:
 *   1. Add an entry to AI_MODELS below
 *   2. Add the backend mapping in server/src/controllers/aiController.js → CLIENT_MODEL_MAP
 *   3. Add the Fal.AI endpoint in server/src/services/ai/falAiService.js → MODEL_ENDPOINTS
 *
 * ── Shape ──────────────────────────────────────────────────────────────────
 * {
 *   id:          string   — Unique key sent to the backend as settings.model
 *   label:       string   — Human-readable name shown in the UI
 *   category:    string   — 'video' | 'image' | 'sound-effects' | 'voiceover'
 *   capabilities: {
 *     imageToVideo:  boolean — Can accept a reference image as start frame
 *     endFrame:      boolean — Can accept an end frame image (in addition to start)
 *     referenceImage: boolean — Can accept a reference/style image (e.g. for image generation)
 *   }
 *   imageSlots:  Array<{ role, label, max? }> | null
 *     — Declares the image upload slots the model supports.
 *       The UI renders a single "+" button; when the user uploads an image
 *       the system auto-assigns the correct role based on the first unfilled slot.
 *       • role:  string — e.g. 'startFrame', 'endFrame', 'reference'
 *       • label: string — Human-readable label shown on the chip (e.g. 'Start', 'End', 'Ref')
 *       • max:   number — Max images for this slot (default 1)
 *   settings: {
 *     aspectRatio:  string[] | null   — e.g. ['16:9','9:16','1:1'], null = not applicable
 *     outputCount:  number[] | null   — e.g. [1,2,3], null = not applicable
 *     length:       string[] | null   — e.g. ['5s','10s'], null = not applicable
 *     voices:       { id, name, previewUrl? }[] | null — for voiceover models
 *   }
 * }
 */

// ── ElevenLabs voices available via Fal.AI ────────────────────────────────
// Source: https://elevenlabs.io/docs/voices (public pre-made voices)
// These are the most popular pre-made voices. The list can be extended
// or fetched dynamically from the backend in the future.
export const ELEVENLABS_VOICES = [
  { id: 'JBFqnCBsd6RMkjVDRZzb', name: 'George',    accent: 'British',    gender: 'Male',   age: 'Middle-aged', style: 'Warm, narrative' },
  { id: 'TX3LPaxmHKxFdv7VOQHJ', name: 'Liam',      accent: 'American',   gender: 'Male',   age: 'Young',       style: 'Articulate, confident' },
  { id: 'pFZP5JQG7iQjIQuC4Bku', name: 'Lily',       accent: 'British',    gender: 'Female', age: 'Middle-aged', style: 'Warm, authoritative' },
  { id: 'XB0fDUnXU5powFXDhCwa', name: 'Charlotte',  accent: 'English-Swedish', gender: 'Female', age: 'Young', style: 'Seductive, calm' },
  { id: 'Xb7hH8MSUJpSbSDYk0k2', name: 'Alice',      accent: 'British',    gender: 'Female', age: 'Middle-aged', style: 'Confident, pleasant' },
  { id: 'iP95p4xoKVk53GoZ742B', name: 'Chris',      accent: 'American',   gender: 'Male',   age: 'Middle-aged', style: 'Casual, conversational' },
  { id: 'nPczCjzI2devNBz1zQrb', name: 'Brian',      accent: 'American',   gender: 'Male',   age: 'Middle-aged', style: 'Deep, narration' },
  { id: 'EXAVITQu4vr4xnSDxMaL', name: 'Sarah',      accent: 'American',   gender: 'Female', age: 'Young',       style: 'Soft, news' },
  { id: 'FGY2WhTYpPnrIDTdsKH5', name: 'Laura',      accent: 'American',   gender: 'Female', age: 'Young',       style: 'Upbeat, social media' },
  { id: 'onwK4e9ZLuTAKqWW03F9', name: 'Daniel',     accent: 'British',    gender: 'Male',   age: 'Middle-aged', style: 'Authoritative, news' },
  { id: 'N2lVS1w4EtoT3dr4eOWO', name: 'Callum',     accent: 'Transatlantic', gender: 'Male', age: 'Middle-aged', style: 'Intense, character' },
  { id: 'IKne3meq5aSn9XLyUdCD', name: 'Charlie',    accent: 'Australian', gender: 'Male',   age: 'Middle-aged', style: 'Natural, conversational' },
  { id: 'cjVigY5qzO86Huf0OWal', name: 'Eric',       accent: 'American',   gender: 'Male',   age: 'Middle-aged', style: 'Friendly, conversational' },
  { id: 'cgSgspJ2msm6clMCkdW9', name: 'Jessica',    accent: 'American',   gender: 'Female', age: 'Young',       style: 'Expressive, conversational' },
  { id: 'bIHbv24MWmeRgasZH58o', name: 'Will',       accent: 'American',   gender: 'Male',   age: 'Young',       style: 'Friendly, young' },
  { id: 'SAz9YHcvj6GT2YYXdXww', name: 'River',      accent: 'American',   gender: 'Non-binary', age: 'Young',   style: 'Confident, smooth' },
];

// ── Model definitions ─────────────────────────────────────────────────────

export const AI_MODELS = [
  // ── Video models ──
  {
    id: 'veo-audio',
    label: 'Veo Audio',
    category: 'video',
    capabilities: { imageToVideo: true, endFrame: false, referenceImage: false },
    imageSlots: [
      { role: 'startFrame', label: 'First frame' },
    ],
    settings: {
      aspectRatio: ['16:9', '9:16', '1:1'],
      outputCount: [1, 2, 3],
      length: ['4s', '6s', '8s'],
      voices: null,
    },
  },
  {
    id: 'veo-silent',
    label: 'Veo Silent',
    category: 'video',
    capabilities: { imageToVideo: true, endFrame: false, referenceImage: false },
    imageSlots: [
      { role: 'startFrame', label: 'First frame' },
    ],
    settings: {
      aspectRatio: ['16:9', '9:16', '1:1'],
      outputCount: [1, 2, 3],
      length: ['4s', '6s', '8s'],
      voices: null,
    },
  },
  {
    id: 'kling-audio',
    label: 'Kling Audio',
    category: 'video',
    capabilities: { imageToVideo: true, endFrame: true, referenceImage: false },
    imageSlots: [
      { role: 'startFrame', label: 'First frame' },
      { role: 'endFrame',   label: 'Last frame' },
    ],
    settings: {
      aspectRatio: ['16:9', '9:16', '1:1'],
      outputCount: [1, 2, 3],
      length: ['5s', '10s'],
      voices: null,
    },
  },
  {
    id: 'kling-silent',
    label: 'Kling Silent',
    category: 'video',
    capabilities: { imageToVideo: true, endFrame: true, referenceImage: false },
    imageSlots: [
      { role: 'startFrame', label: 'First frame' },
      { role: 'endFrame',   label: 'Last frame' },
    ],
    settings: {
      aspectRatio: ['16:9', '9:16', '1:1'],
      outputCount: [1, 2, 3],
      length: ['5s', '10s'],
      voices: null,
    },
  },

  // ── Image models ──
  {
    id: 'nano-banana',
    label: 'Nano Banana',
    category: 'image',
    capabilities: { imageToVideo: false, endFrame: false, referenceImage: true },
    imageSlots: [
      { role: 'reference', label: 'Ref', max: 5 },
    ],
    settings: {
      aspectRatio: ['16:9', '9:16', '1:1'],
      outputCount: [1, 2, 3],
      length: null,
      voices: null,
    },
  },

  // ── Sound Effects (Stable Audio) ──
  {
    id: 'sound-effects',
    label: 'Sound Effects',
    category: 'sound-effects',
    capabilities: { imageToVideo: false, endFrame: false, referenceImage: false },
    imageSlots: null,
    settings: {
      aspectRatio: null,
      outputCount: null,
      length: ['5s', '10s'],
      voices: null,
    },
  },

  // ── Voiceover (ElevenLabs TTS) ──
  {
    id: 'voiceover',
    label: 'Voiceover',
    category: 'voiceover',
    capabilities: { imageToVideo: false, endFrame: false, referenceImage: false },
    imageSlots: null,
    settings: {
      aspectRatio: null,
      outputCount: null,
      length: null,          // Duration is character-based, not user-selectable
      voices: ELEVENLABS_VOICES,
    },
  },
];

// ── Reverse mapping: provider/backend model key → client model ID ────────
// The server replaces settings.model with a provider key (e.g. 'veo-3')
// so stored jobs don't contain the original client model ID.
// For ambiguous cases (veo-3 → veo-audio or veo-silent) we default to the
// audio variant.
const PROVIDER_TO_CLIENT_MODEL = {
  'veo-3':              'veo-audio',
  'veo-3-img2vid':      'veo-audio',
  'kling-v2.6-master':  'kling-audio',
  'kling-v3-img2vid':   'kling-audio',
  'nanobanana':         'nano-banana',
  'nanobanana-edit':    'nano-banana',
  'stable-audio':       'sound-effects',
  'elevenlabs-tts':     'voiceover',
};

// ── Helpers ────────────────────────────────────────────────────────────────

/** Get a model config by its id */
export const getModelById = (id) => AI_MODELS.find((m) => m.id === id) || null;

/**
 * Resolve a settings.model value (which may be a provider key) back to a
 * client model ID.  Uses settings.generateAudio to disambiguate audio vs
 * silent variants that share the same provider key.
 */
export const resolveClientModel = (settings) => {
  if (!settings) return null;
  if (getModelById(settings.model)) return settings.model;

  let clientModel = PROVIDER_TO_CLIENT_MODEL[settings.model] || null;

  // The default mapping always picks the audio variant.  If the job was
  // actually generated in silent mode, swap to the silent variant.
  if (clientModel && settings.generateAudio === false) {
    const AUDIO_TO_SILENT = {
      'veo-audio':  'veo-silent',
      'kling-audio': 'kling-silent',
    };
    if (AUDIO_TO_SILENT[clientModel]) {
      clientModel = AUDIO_TO_SILENT[clientModel];
    }
  }

  return clientModel;
};

/** Get all models in a given category */
export const getModelsByCategory = (category) => AI_MODELS.filter((m) => m.category === category);

/** Get the default voice for voiceover models */
export const getDefaultVoiceId = () => ELEVENLABS_VOICES[0]?.id || null;

/**
 * Check whether a setting is applicable for a given model.
 * Returns the allowed values array, or null if not applicable.
 */
export const getSettingOptions = (modelId, settingKey) => {
  const model = getModelById(modelId);
  if (!model) return null;
  return model.settings[settingKey] ?? null;
};

/** Check if a model supports image-to-video (start frame) */
export const supportsImageToVideo = (modelId) => {
  const model = getModelById(modelId);
  return model?.capabilities?.imageToVideo ?? false;
};

/** Check if a model supports an end frame image */
export const supportsEndFrame = (modelId) => {
  const model = getModelById(modelId);
  return model?.capabilities?.endFrame ?? false;
};

/** Check if a model supports reference images */
export const supportsReferenceImage = (modelId) => {
  const model = getModelById(modelId);
  return model?.capabilities?.referenceImage ?? false;
};

/**
 * Get the image slots declared by a model.
 * Returns an array of { role, label, max } or an empty array if the model
 * doesn't accept any images.
 */
export const getImageSlots = (modelId) => {
  const model = getModelById(modelId);
  return model?.imageSlots || [];
};

/**
 * Check whether a model accepts any image uploads at all.
 * True if the model has at least one imageSlot defined.
 */
export const acceptsImageUpload = (modelId) => {
  return getImageSlots(modelId).length > 0;
};

/**
 * Given the current list of attached reference media, determine the role
 * that the next uploaded image should be assigned for the given model.
 * Returns the role string (e.g. 'startFrame', 'reference') or null if
 * all slots are filled.
 */
export const getNextImageRole = (modelId, currentMedia) => {
  const slots = getImageSlots(modelId);
  if (slots.length === 0) return null;

  for (const slot of slots) {
    const max = slot.max || 1;
    const filled = (currentMedia || []).filter(m => m.role === slot.role).length;
    if (filled < max) return slot.role;
  }

  return null; // all slots filled
};

/**
 * Get the human-readable label for a given image role in a model.
 */
export const getImageRoleLabel = (modelId, role) => {
  const slots = getImageSlots(modelId);
  const slot = slots.find(s => s.role === role);
  return slot?.label || role;
};
